import { getApp } from "firebase/app";
import {
  doc,
  getDoc,
  getFirestore,
  increment,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";

import { Post } from "@/types/post";

const firestore = getFirestore(getApp());

export type UseLikeActionResult = {
  liked: boolean;
  likesCount: number;
  /** Toggles the viewer's like and writes via batch. No-op when viewerUid is undefined — callers handle the unauthenticated path themselves (e.g. redirect to login). */
  toggleLike: () => Promise<void>;
};

/**
 * Manages a single post's like state for the current viewer.
 *
 * One-shot read of `posts/{postId}/likes/{viewerUid}` on mount (no listener).
 * Optimistic flip on toggle, batched write, rollback on error. The denormalized
 * likes count is sourced from `post.likes` and re-anchored whenever that prop
 * changes (silent focus refresh in PostsContext brings fresh values via
 * `refreshVisibleCounts`). `post.likes` also acts as an invalidation signal for
 * the viewer-liked doc — every like/unlike write bumps that count in the same
 * batch, so when it changes we re-read the personal like doc to keep the heart
 * icon in sync with the count.
 */
export function useLikeAction(
  post: Post,
  viewerUid: string | undefined,
): UseLikeActionResult {
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes || 0);

  // Flipped on toggle so a still-pending getDoc result can't clobber the
  // optimistic flip — the one-shot read replaces the self-healing onSnapshot
  // path D1 removed, so this guard is what keeps the two from racing.
  const staleRef = useRef(false);

  useEffect(() => {
    setLikesCount(post.likes || 0);
  }, [post.likes]);

  useEffect(() => {
    if (!viewerUid) {
      setLiked(false);
      return;
    }
    let cancelled = false;
    staleRef.current = false;
    const likeRef = doc(firestore, "posts", post.id, "likes", viewerUid);
    getDoc(likeRef)
      .then((snap) => {
        if (!cancelled && !staleRef.current) {
          setLiked(!!snap?.exists?.());
        }
      })
      .catch(() => {
        // Silent — a failed read just leaves liked=false. The user can still
        // tap the heart; the batch below will either succeed or roll back.
      });
    return () => {
      cancelled = true;
    };
  }, [post.id, post.likes, viewerUid]);

  const toggleLike = useCallback(async () => {
    if (!viewerUid) return;

    staleRef.current = true;

    const postRef = doc(firestore, "posts", post.id);
    const likeRef = doc(firestore, "posts", post.id, "likes", viewerUid);
    const userLikesRef = doc(
      firestore,
      "users",
      viewerUid,
      "likedPosts",
      post.id,
    );
    const batch = writeBatch(firestore);

    const newLiked = !liked;
    setLiked(newLiked);
    setLikesCount((count) => count + (newLiked ? 1 : -1));

    try {
      if (newLiked) {
        batch.set(likeRef, { createdAt: serverTimestamp() });
        batch.set(userLikesRef, { createdAt: serverTimestamp() });
        batch.update(postRef, { likes: increment(1) });
      } else {
        batch.delete(likeRef);
        batch.delete(userLikesRef);
        batch.update(postRef, { likes: increment(-1) });
      }
      await batch.commit();
    } catch (error) {
      console.error("Error toggling like:", error);
      setLiked(!newLiked);
      setLikesCount((count) => count - (newLiked ? 1 : -1));
    }
  }, [liked, post.id, viewerUid]);

  return { liked, likesCount, toggleLike };
}
